import { useState, useEffect } from 'react';
import { Link2, Save, RefreshCw, CheckCircle, AlertCircle, Network } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import { useTenantStore } from '../../../stores/tenantStore';
import { usePermissions } from '../../../hooks/usePermissions';
// DSCP components removed — DSCP classification is now managed through
// the Unified Policy Engine (Routing Policies page)

interface ServiceChainSettingsProps {
  edgeId: string;
}

interface ServiceChainConfig {
  enabled: boolean;
  chain_mode: 'tap' | 'bypass';
  input_interface: string;
  output_interface: string;
  traffic_selector: 'all' | 'wan' | 'lan' | 'custom';
  fallback_action: 'forward' | 'drop';
}

const defaultConfig: ServiceChainConfig = {
  enabled: false,
  chain_mode: 'tap',
  input_interface: 'tap-suricata0',
  output_interface: 'tap-suricata1',
  traffic_selector: 'wan',
  fallback_action: 'forward',
};

const ServiceChainSettings = ({ edgeId }: ServiceChainSettingsProps) => {
  const { canUpdate } = usePermissions();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [config, setConfig] = useState<ServiceChainConfig>(defaultConfig);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    fetchData();
  }, [edgeId, selectedTenantId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const configRes = await api.get(`/api/v2/edges/${edgeId}/service-chain/config`);
      if (configRes.data?.config) {
        const apiConfig = configRes.data.config;
        // Migrate legacy chain modes to current options
        let chainMode = apiConfig.chain_mode || defaultConfig.chain_mode;
        if (chainMode === 'tap-afxdp' || chainMode === 'inline') {
          chainMode = 'tap';
        }
        setConfig({
          ...defaultConfig,
          ...apiConfig,
          chain_mode: chainMode,
          enabled: !!apiConfig.enabled,
        });
      }
    } catch (error) {
      console.error('Failed to fetch service chain data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field: keyof ServiceChainConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await api.put(`/api/v2/edges/${edgeId}/service-chain/config`, config);
      toast.success('Service chain configuration saved');
      setHasChanges(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Link2 className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Service Chain & DSCP Routing</h3>
        </div>
        <div className="flex items-center space-x-2">
          {config.enabled ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Active ({config.chain_mode})
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <AlertCircle className="w-3 h-3 mr-1" />
              Disabled
            </span>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <Network className="w-5 h-5 text-blue-400 mr-2 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Application-Aware Routing</p>
            <p className="mt-1">
              Service chaining routes traffic through Suricata for deep packet inspection.
              Applications are identified and marked with DSCP values, enabling application-aware
              routing policies (e.g., VoIP via low-latency WAN, video via high-bandwidth WAN).
            </p>
          </div>
        </div>
      </div>

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="font-medium text-gray-900">Enable Service Chain</label>
          <p className="text-sm text-gray-500">Route traffic through Suricata for application identification</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleConfigChange('enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {config.enabled && (
        <>
          {/* Chain Mode & Traffic Selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chain Mode</label>
              <select
                value={config.chain_mode}
                onChange={(e) => handleConfigChange('chain_mode', e.target.value)}
                className="input-base w-full"
              >
                <option value="tap">TAP</option>
                <option value="bypass">Bypass</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Traffic redirected through TAP interfaces to Suricata AF_PACKET for deep inspection
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Traffic Selector</label>
              <select
                value={config.traffic_selector}
                onChange={(e) => handleConfigChange('traffic_selector', e.target.value)}
                className="input-base w-full"
              >
                <option value="all">All Traffic</option>
                <option value="wan">WAN Traffic Only</option>
                <option value="lan">LAN Traffic Only</option>
                <option value="custom">Custom (via ACL)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Which traffic to route through the service chain
              </p>
            </div>
          </div>

          {/* DSCP notice */}
          <div className="border-t pt-6">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 text-sm text-blue-700">
              <p className="font-medium">DSCP Classification</p>
              <p className="mt-1">
                DSCP application mappings and routing policies are now managed through the
                unified Routing Policies system. Use the Routing Policies tab to configure
                DSCP match/mark rules alongside L3/L4 and domain-based policies.
              </p>
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="border-t pt-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
            </button>

            {showAdvanced && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Input Interface (VPP tap100)
                    </label>
                    <input
                      type="text"
                      value={config.input_interface}
                      readOnly
                      className="input-base w-full bg-gray-100 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Output Interface (VPP tap101)
                    </label>
                    <input
                      type="text"
                      value={config.output_interface}
                      readOnly
                      className="input-base w-full bg-gray-100 text-gray-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fallback Action</label>
                  <select
                    value={config.fallback_action}
                    onChange={(e) => handleConfigChange('fallback_action', e.target.value)}
                    className="input-base w-full md:w-1/3"
                  >
                    <option value="forward">Forward (Allow)</option>
                    <option value="drop">Drop (Block)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Action if Suricata service is unavailable
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={handleSaveConfig}
            disabled={saving || !hasChanges}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      )}

      {hasChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <p className="text-sm text-amber-700">
            You have unsaved changes. Click "Save Configuration" to save, then sync the edge config
            for changes to take effect.
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceChainSettings;
